import type { AlmightyReferenceKind } from './almightyReference'

export type AlmightyMentionReferenceItem = {
    id: string
    kind: AlmightyReferenceKind
    url?: string
    name?: string
    duration?: number
    loading?: boolean
}

export type AlmightyMentionAsset = {
    id: string
    kind: AlmightyReferenceKind
    label: string
    protocolLabel?: string
    previewUrl?: string
}

export type AlmightyPromptMentionToken =
    | {
        type: 'text'
        text: string
    }
    | {
        type: 'mention'
        assetId: string
        kind: AlmightyReferenceKind
        fallbackLabel: string
    }

const DEFAULT_KIND_LABEL: Record<AlmightyReferenceKind, string> = {
    image: '图片',
    video: '视频',
    audio: '音频',
}

export const getAlmightyMentionLabel = (
    kind: AlmightyReferenceKind,
    index: number,
    t?: (key: string, params?: Record<string, any>) => string,
    useProtocolLabel = false
) => {
    if (useProtocolLabel) {
        const protocolKind: Record<AlmightyReferenceKind, string> = {
            image: 'Image',
            video: 'Video',
            audio: 'Audio',
        }
        return `${protocolKind[kind]} ${index}`
    }
    const translated = t?.(`workbench.uploadLabels.${kind}`)
    const baseLabel = translated && translated !== `workbench.uploadLabels.${kind}`
        ? translated
        : DEFAULT_KIND_LABEL[kind]
    return `${baseLabel}${index}`
}

export const buildAlmightyMentionAssets = (
    items: AlmightyMentionReferenceItem[],
    t?: (key: string, params?: Record<string, any>) => string,
    useProtocolLabels = false
): AlmightyMentionAsset[] => {
    // 这里的编号永远按当前上传区顺序重新计算。
    // 这样拖拽后会得到：
    // - mention/chip 位置不变（token 顺序不变）
    // - 但每个素材显示的“图片1/图片2/视频1...”会按新顺序刷新
    const counters: Record<AlmightyReferenceKind, number> = {
        image: 0,
        video: 0,
        audio: 0,
    }

    return (items || [])
        .filter(item =>
            item &&
            typeof item.id === 'string' &&
            ['image', 'video', 'audio'].includes(item.kind) &&
            typeof item.url === 'string' &&
            !!item.url
        )
        .map(item => {
            counters[item.kind] += 1
            const kindIndex = counters[item.kind]
            return {
                id: item.id,
                kind: item.kind,
                label: getAlmightyMentionLabel(item.kind, kindIndex, t),
                ...(useProtocolLabels
                    ? {
                        protocolLabel: getAlmightyMentionLabel(
                            item.kind,
                            kindIndex,
                            t,
                            true
                        ),
                    }
                    : {}),
                previewUrl: item.kind === 'audio' ? undefined : item.url,
            }
        })
}

const mergeAdjacentTextTokens = (tokens: AlmightyPromptMentionToken[]) => {
    const merged: AlmightyPromptMentionToken[] = []

    for (const token of tokens) {
        if (token.type === 'text') {
            if (!token.text) continue
            const prev = merged[merged.length - 1]
            if (prev?.type === 'text') {
                prev.text += token.text
            } else {
                merged.push({ ...token })
            }
            continue
        }
        if (!token.assetId || !token.fallbackLabel) continue
        merged.push({ ...token })
    }

    return merged
}

export const normalizeAlmightyPromptMentionTokens = (
    tokens?: AlmightyPromptMentionToken[] | null,
    fallbackPrompt = ''
) => {
    const normalized = Array.isArray(tokens)
        ? mergeAdjacentTextTokens(
            tokens
                .filter((token): token is AlmightyPromptMentionToken =>
                    !!token && (token.type === 'text' || token.type === 'mention')
                )
                .map(token => {
                    if (token.type === 'text') {
                        return {
                            type: 'text' as const,
                            text: typeof token.text === 'string' ? token.text : '',
                        }
                    }

                    return {
                        type: 'mention' as const,
                        assetId: typeof token.assetId === 'string' ? token.assetId : '',
                        kind: token.kind,
                        fallbackLabel:
                            typeof token.fallbackLabel === 'string'
                                ? token.fallbackLabel
                                : '',
                    }
                })
        )
        : []

    if (normalized.length > 0) {
        return normalized
    }

    if (fallbackPrompt) {
        return [{ type: 'text' as const, text: fallbackPrompt }]
    }

    return []
}

export const cloneAlmightyPromptMentionTokens = (
    tokens?: AlmightyPromptMentionToken[] | null,
    fallbackPrompt = ''
) => {
    return normalizeAlmightyPromptMentionTokens(tokens, fallbackPrompt).map(token =>
        token.type === 'text'
            ? { type: 'text' as const, text: token.text }
            : {
                type: 'mention' as const,
                assetId: token.assetId,
                kind: token.kind,
                fallbackLabel: token.fallbackLabel,
            }
    )
}

export const pruneAlmightyPromptMentionTokensByAssetIds = (
    tokens?: AlmightyPromptMentionToken[] | null,
    validAssetIds?: Iterable<string>
) => {
    const validIds = new Set(validAssetIds || [])
    return cloneAlmightyPromptMentionTokens(
        normalizeAlmightyPromptMentionTokens(tokens).filter(token =>
            token.type === 'text' || validIds.has(token.assetId)
        )
    )
}

export const serializeAlmightyPromptMentionTokens = (
    tokens?: AlmightyPromptMentionToken[] | null,
    assets: AlmightyMentionAsset[] = []
) => {
    const assetLabelMap = new Map(
        assets.map(asset => [asset.id, asset.protocolLabel || asset.label])
    )

    return normalizeAlmightyPromptMentionTokens(tokens)
        .map(token => {
            if (token.type === 'text') return token.text
            return assetLabelMap.get(token.assetId) || token.fallbackLabel
        })
        .join('')
}

/** Serialize tokens using the labels shown inside the contenteditable editor. */
export const serializeAlmightyPromptMentionDisplayTokens = (
    tokens?: AlmightyPromptMentionToken[] | null,
    assets: AlmightyMentionAsset[] = []
) => {
    const assetLabelMap = new Map(assets.map(asset => [asset.id, asset.label]))

    return normalizeAlmightyPromptMentionTokens(tokens)
        .map(token => {
            if (token.type === 'text') return token.text
            return assetLabelMap.get(token.assetId) || token.fallbackLabel
        })
        .join('')
}

export const resolveAlmightyMentionContext = (value: string, caretPosition: number) => {
    const safeCaret = Math.max(0, Math.min(caretPosition, value.length))
    const before = value.slice(0, safeCaret)
    const start = before.lastIndexOf('@')
    if (start < 0) return null

    const query = before.slice(start + 1)
    if (/\s/.test(query)) return null

    return { query, start, end: safeCaret }
}

export const localizeAlmightyProtocolPrompt = (
    prompt?: string | null,
    _t?: (key: string, params?: Record<string, any>) => string,
    assets: AlmightyMentionAsset[] = []
) => {
    if (!prompt) return ''
    const replacements = assets
        .filter(asset => !!asset.protocolLabel && !!asset.label)
        .sort((left, right) =>
            String(right.protocolLabel).length - String(left.protocolLabel).length
        )

    return replacements.reduce((text, asset) => {
        const protocolLabel = String(asset.protocolLabel)
        const escapedLabel = protocolLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const pattern = new RegExp(
            `(^|[^A-Za-z0-9_])${escapedLabel}(?=$|[^A-Za-z0-9_])`,
            'g'
        )
        return text.replace(pattern, (_, prefix) => `${prefix}${asset.label}`)
    }, prompt)
}

export const createAlmightyPromptTextTokens = (prompt = '') => {
    return prompt ? [{ type: 'text' as const, text: prompt }] : []
}
